import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Container } from 'reactstrap';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { getApiUrl, getMediaUrl, siteConfig } from '../utils/site';
import heroImage from '../assets/all-images/bg13.jpg';

const fadeUp = { hidden:{opacity:0,y:28}, visible:{opacity:1,y:0,transition:{duration:0.65}} };
const stagger = { visible:{transition:{staggerChildren:0.1}} };

const DesignGallery = () => {
  const [designs, setDesigns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(getApiUrl('/api/designs'))
      .then(res => setDesigns(res.data.data || res.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <>
      <Helmet>
        <title>Product Gallery | Ethiopian Gemstone Jewellery | Roha Jewellery</title>
        <meta name="description" content="Browse Roha Jewellery's exclusive product gallery featuring Ethiopian opal, emerald, ruby and gold jewellery designs. Get inspired by our handcrafted Habesha collections." />
        <link rel="canonical" href={`${siteConfig.url}/designs`} />
      </Helmet>

      <main className="roha-page">
        <section style={{ position:"relative", minHeight:"55vh", display:"flex", alignItems:"center", overflow:"hidden", background:"var(--color-dark)" }}>
          <div style={{ position:"absolute", inset:0 }}>
            <img src={heroImage} alt="" aria-hidden="true" style={{ width:"100%", height:"100%", objectFit:"cover", opacity:0.4 }} />
            <div style={{ position:"absolute", inset:0, background:"linear-gradient(135deg, rgba(6,14,13,0.92) 0%, rgba(6,14,13,0.6) 100%)" }} />
          </div>
          <Container style={{ position:"relative", zIndex:1, padding:"7rem 0 4rem" }}>
            <p className="roha-eyebrow">Product Gallery</p>
            <h1 style={{ fontFamily:"var(--font-heading)", fontSize:"clamp(2.5rem,5vw,4.5rem)", fontWeight:300, color:"var(--color-heading)", maxWidth:700, lineHeight:1.1, marginBottom:"1.25rem" }}>
              Ethiopian Jewellery Designs, Crafted with Heritage
            </h1>
            <p style={{ color:"rgba(245,240,232,0.7)", maxWidth:520, marginBottom:"2rem", lineHeight:1.8 }}>
              Explore our curated gallery of Ethiopian gemstone jewellery designs — from luminous opals to Habesha gold masterpieces.
            </p>
            <div style={{ display:"flex", gap:"1rem", flexWrap:"wrap" }}>
              <Link to="/design-request" className="roha-button roha-button--solid">Request Custom Design</Link>
              <Link to="/products" className="roha-button roha-button--ghost">Shop Collection</Link>
            </div>
          </Container>
        </section>

        <section className="roha-section">
          <Container>
            <div className="roha-section__heading roha-section__heading--row">
              <div>
                <p className="roha-eyebrow">Our Designs</p>
                <h2>Handcrafted Ethiopian Jewellery Gallery</h2>
              </div>
              <Link to="/design-request" className="roha-text-link">Request a design</Link>
            </div>

            {loading ? (
              <div className="roha-empty-state">Loading designs...</div>
            ) : designs.length === 0 ? (
              <div className="roha-empty-state">No designs available yet.</div>
            ) : (
              <motion.div className="roha-gallery-preview" initial="hidden" animate="visible" variants={stagger}>
                {designs.map(design => (
                  <motion.article key={design.id} className="roha-gallery-preview__card" variants={fadeUp}
                    whileHover={{ y: -6, transition: { duration: 0.3 } }}>
                    {design.image_url && (
                      <img src={getMediaUrl(design.image_url)} alt={design.title} loading="lazy"
                        onError={e => { e.currentTarget.onerror=null; e.currentTarget.src="/image.jpg"; }} />
                    )}
                    <div>
                      <strong>{design.title}</strong>
                      {design.description && <p>{design.description}</p>}
                    </div>
                  </motion.article>
                ))}
              </motion.div>
            )}

            <div style={{ textAlign:"center", marginTop:"3rem" }}>
              <Link to="/design-request" className="roha-button roha-button--solid">Create Your Own Design</Link>
            </div>
          </Container>
        </section>
      </main>
    </>
  );
};

export default DesignGallery;
